import { NgZone } from '@angular/core';
import { Observable, Observer, queueScheduler, SchedulerAction, SchedulerLike, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { cancelExecuteLater, executeLater, safeUnsubscribe } from './../../utils';

/* Copyright IBM Corp. 2017 */

/**
 * Constructs a callback function that completes the subject and then unsubscribes all pending subscriptions
 *
 * @param aSubject  the subject
 * @return the callback
 */
function _completeLater<T>(aSubject: Subject<T>): () => void {
  return aSubject.complete.bind(aSubject);
}

/*
 * Returns a function that will unsubscribe to the given subscription if invoked.
 *
 * @param aSubscription   the subscription
 * @return the function that will unsubscribe
 */
function _unsubscribeLater(aSubscription: Subscription | null | undefined): () => void {
  return () => safeUnsubscribe(aSubscription);
}

/**
 * Implements a scheduler that runs the work inside or outside an angular zone.
 */
abstract class AbstractNgInZoneScheduler implements SchedulerLike {

  private delegate: SchedulerLike;

  private zone: NgZone;

  constructor(aZone: NgZone, aDelegate?: SchedulerLike) {
    this.delegate = aDelegate || queueScheduler;
    this.zone = aZone;
  }

  // simply dispatche
  now(): number {
    return this.delegate.now();
  }

  schedule<T>(work: (this: SchedulerAction<T>, state?: T) => void, delay?: number, state?: T): Subscription {
    // dispatch
    const delegate = this.delegate;
    const zone = this.zone;
    return this.doRun(zone, () => delegate.schedule(work, delay, state));
  }

  protected abstract doRun(zone: NgZone, cb: () => Subscription): Subscription;
}


class NgInZoneScheduler extends AbstractNgInZoneScheduler {

  constructor(aZone: NgZone, aDelegate?: SchedulerLike) {
    super(aZone, aDelegate);
  }

  protected doRun(zone: NgZone, cb: () => Subscription): Subscription {
    return <Subscription>zone.run(cb);
  }
}

class NgOutOfZoneScheduler extends AbstractNgInZoneScheduler {

  constructor(aZone: NgZone, aDelegate?: SchedulerLike) {
    super(aZone, aDelegate);
  }

  protected doRun(zone: NgZone, cb: () => Subscription): Subscription {
    return <Subscription>zone.runOutsideAngular(cb);
  }
}

export interface ZoneSchedulers {
  inside: SchedulerLike;
  outside: SchedulerLike;
}

function _createZoneSchedulers(aZone: NgZone, aDelegate?: SchedulerLike): ZoneSchedulers {
  // construct the schedulers for our zone
  const result: ZoneSchedulers = {
    inside: new NgInZoneScheduler(aZone, aDelegate),
    outside: new NgOutOfZoneScheduler(aZone, aDelegate)
  };
  // returns the scheduler
  return result;
}

function _distinctSubject<T>(aSubject: Subject<T>): Observable<T> {
  // attach
  return aSubject.pipe(distinctUntilChanged());
}


/**
 * Produces a value at a later point in time on top of a generator
 *
 * @param aGenerator the generator function
 * @return a function that takes a parameter and generates an observable based on the result
 */
function _createLater<K, O, T>(aGenerator: (aKey: K, aOpt?: O) => T): (aKey: K, aOpt?: O) => Observable<T> {
  // capture the arguments
  const that = this;
  // construct
  function _callback(aKey: K, aOpt: O) {
    // arguments to the callback
    const args = arguments;
    // create the lazy observable
    return Observable.create((aObserver: Observer<T>) => {
      // schedule the call
      const handle = executeLater(() => {
        try {
          // dispatch the value
          aObserver.next(aGenerator.apply(that, args));
          aObserver.complete();
        } catch (error) {
          // handle error
          aObserver.error(error);
        }
      }
      );
      // return the cancel function
      return () => cancelExecuteLater(handle);
    });
  }

  // returns the callback
  return _callback;
}

export {
  _unsubscribeLater as unsubscribeLater,
  _completeLater as completeLater,
  _createZoneSchedulers as createZoneSchedulers,
  _distinctSubject as distinctSubject,
  _createLater as createLater
};

